import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { NotificationsApiService } from './NotificationsApiService';
import { type InAppNotification, type JobRunEntry, isTerminalJob } from './NotificationTypes';

/** How often the closed tray re-checks the badge (unread count + active jobs). */
const BADGE_POLL_INTERVAL_MS = 60_000;

/** How often the *open* tray re-polls jobs so live progress advances. */
const JOB_POLL_INTERVAL_MS = 5_000;

/** How many recent jobs the tray keeps. */
const JOB_PAGE_SIZE = 30;

/**
 * One row in the unified activity feed: either a notification (which may carry the
 * job run(s) that produced it, for an expandable detail) or a standalone job that
 * has no notification of its own yet (typically one still running).
 */
export type ActivityItem =
    | { readonly kind: 'notification'; readonly id: string; readonly at: string; readonly notification: InAppNotification; readonly jobs: JobRunEntry[] }
    | { readonly kind: 'job'; readonly id: string; readonly at: string; readonly job: JobRunEntry };

/** A job run's correlation id — the join key back to the notification it produced. */
function jobCorrelationId(job: JobRunEntry): string {
    return job.correlationId ?? '';
}

/**
 * State behind the header bell — now a combined activity tray of notifications
 * and the user's own background jobs.
 *
 * Two cadences: a cheap badge poll every minute keeps the unread count and the
 * active-job indicator fresh while the tray is closed; opening the tray adds a
 * fast job poll so a running sync's progress advances in view. Both stop when
 * the tray closes. Failures are swallowed into `failed`/`jobsFailed` rather than
 * thrown — a notifications outage must not break the app shell.
 */
@injectable()
export class NotificationTrayViewModel extends BaseViewModel {
    private notificationList: InAppNotification[] = [];
    private jobList: JobRunEntry[] = [];
    private unread = 0;
    private isLoading = false;
    private hasFailed = false;
    private jobsHaveFailed = false;
    private badgeTimer: ReturnType<typeof setInterval> | null = null;
    private jobTimer: ReturnType<typeof setInterval> | null = null;

    constructor(@inject(NotificationsApiService) private readonly api: NotificationsApiService) {
        super();
    }

    get notifications(): InAppNotification[] {
        return this.notificationList;
    }

    get jobs(): JobRunEntry[] {
        return this.jobList;
    }

    /**
     * The unified activity feed, newest first. Notifications carry any job runs
     * that share their correlation id (for the expandable job detail); a job is
     * shown on its own only when no notification represents it yet — so a job that
     * has fired a completion notification collapses into that notification rather
     * than appearing twice.
     */
    get activityItems(): ActivityItem[] {
        const jobsByCorrelation = new Map<string, JobRunEntry[]>();
        for (const job of this.jobList) {
            const correlationId = jobCorrelationId(job);
            const group = jobsByCorrelation.get(correlationId) ?? [];
            group.push(job);
            jobsByCorrelation.set(correlationId, group);
        }

        const claimed = new Set<string>();
        const items: ActivityItem[] = [];
        for (const notification of this.notificationList) {
            const correlationId = notification.correlationId ?? '';
            const jobs = correlationId.length > 0 ? (jobsByCorrelation.get(correlationId) ?? []) : [];
            if (correlationId.length > 0) claimed.add(correlationId);
            items.push({ kind: 'notification', id: `n:${notification.id}`, at: notification.createdAt, notification, jobs });
        }
        for (const job of this.jobList) {
            if (claimed.has(jobCorrelationId(job))) continue;
            items.push({ kind: 'job', id: `j:${job.jobRunId}`, at: job.completedAt ?? job.startedAt, job });
        }

        return items.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
    }

    get unreadCount(): number {
        return this.unread;
    }

    /** Jobs still running — drives the processing indicator and the Jobs-tab count. */
    get activeJobCount(): number {
        return this.jobList.filter((job) => !isTerminalJob(job.status)).length;
    }

    get loading(): boolean {
        return this.isLoading;
    }

    get failed(): boolean {
        return this.hasFailed;
    }

    get jobsFailed(): boolean {
        return this.jobsHaveFailed;
    }

    /** Start badge polling. Safe to call repeatedly; only the first arms a timer. */
    startPolling(): void {
        if (this.badgeTimer !== null) return;
        void this.refreshBadge();
        this.badgeTimer = setInterval(() => void this.refreshBadge(), BADGE_POLL_INTERVAL_MS);
    }

    /**
     * Track tray open/close. Opening loads the full feed and arms a fast job poll
     * so live progress advances; closing tears the fast poll down.
     */
    setTrayOpen(open: boolean): void {
        if (open) {
            void this.loadFeed();
            if (this.jobTimer === null) {
                this.jobTimer = setInterval(() => void this.refreshJobs(), JOB_POLL_INTERVAL_MS);
            }
        } else if (this.jobTimer !== null) {
            clearInterval(this.jobTimer);
            this.jobTimer = null;
        }
    }

    /** The cheap tick behind a closed tray — unread count plus a jobs refresh for the indicator. */
    async refreshBadge(): Promise<void> {
        await Promise.all([this.refreshUnreadCount(), this.refreshJobs()]);
    }

    async refreshUnreadCount(): Promise<void> {
        try {
            this.unread = await this.api.getUnreadCount();
            this.hasFailed = false;
        } catch {
            this.hasFailed = true;
        }
        this.notifyChange();
    }

    async refreshJobs(): Promise<void> {
        try {
            const page = await this.api.listMyJobs(JOB_PAGE_SIZE);
            this.jobList = page.entries;
            this.jobsHaveFailed = false;
        } catch {
            this.jobsHaveFailed = true;
        }
        this.notifyChange();
    }

    async loadFeed(): Promise<void> {
        this.isLoading = true;
        this.notifyChange();
        try {
            this.notificationList = await this.api.listNotifications();
            this.unread = this.notificationList.filter((n) => n.readAt === null).length;
            this.hasFailed = false;
        } catch {
            this.hasFailed = true;
        }
        this.isLoading = false;
        this.notifyChange();
        await this.refreshJobs();
    }

    /**
     * Mark one notification read. The row and badge update optimistically so the
     * tray responds instantly; a failed call reverts by re-reading the feed.
     */
    async markRead(notificationId: string): Promise<void> {
        const target = this.notificationList.find((n) => n.id === notificationId);
        if (!target || target.readAt !== null) return;

        const optimisticReadAt = new Date().toISOString();
        this.applyRead(notificationId, optimisticReadAt);
        this.notifyChange();

        try {
            await this.api.markRead(notificationId);
        } catch {
            await this.loadFeed();
        }
    }

    async markAllRead(): Promise<void> {
        if (this.unread === 0) return;

        const optimisticReadAt = new Date().toISOString();
        const previous = this.notificationList;
        this.notificationList = this.notificationList.map((n) =>
            n.readAt === null ? { ...n, readAt: optimisticReadAt } : n,
        );
        this.unread = 0;
        this.notifyChange();

        try {
            await this.api.markAllRead();
        } catch {
            this.notificationList = previous;
            await this.refreshUnreadCount();
        }
    }

    override dispose(): void {
        if (this.badgeTimer !== null) {
            clearInterval(this.badgeTimer);
            this.badgeTimer = null;
        }
        if (this.jobTimer !== null) {
            clearInterval(this.jobTimer);
            this.jobTimer = null;
        }
        super.dispose();
    }

    private applyRead(notificationId: string, readAt: string): void {
        this.notificationList = this.notificationList.map((n) =>
            n.id === notificationId ? { ...n, readAt } : n,
        );
        this.unread = Math.max(0, this.unread - 1);
    }
}
